import { yupResolver } from "@hookform/resolvers/yup";
import type { ProductConfigurationVisualProps } from "@omni/types/components/productConfiguration/productConfiguration";
import { CalculatorStyles } from "@omni/ui/brand/productConfiguration/calculator/calculator.styles";
import { Button } from "@omni/ui/components/Button/button.component";
import { Divider } from "@omni/ui/components/Divider/divider.component";
import { entries } from "@omni/utils/object.utils";
import { cn } from "@omni/utils/styles";
import { useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { InferType } from "yup";

import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Price } from "@/components/Price/price";
import { useProductDetail } from "@/components/Product/ECommerce/hooks/useProductDetail.hook";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { isValidPositiveNumber } from "@/utils/calculators";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";
import { formatPrice } from "@/utils/price.utils";
import { getFeatures } from "@/utils/productConfiguration/visuals/calculator/helpers/features.helpers";

import { CalculatorConfig, CalculatorSchema } from "./calculator.types";
import { CalculatorField } from "./components/calculatorField.component";
import { calculate } from "./helpers/calculation.helpers";

const FORM_ID = "calculator-form";

type Props<TSchema extends CalculatorSchema> =
    ProductConfigurationVisualProps<{}> & {
        config: CalculatorConfig<TSchema>;
    };

export const Calculator = <TSchema extends CalculatorSchema>({
    config,
    setConfigurationValid,
    metaConfiguration: { product } = {},
    className
}: Props<TSchema>) => {
    const { t } = useGranite();

    const { updateProductDetail } = useProductDetail();
    const [calculatedQuantity, setCalculatedQuantity] = useState<number>();

    const features = useMemo(() => {
        return getFeatures(product, config);
    }, [product, config]);

    const fieldEntries = useMemo(() => {
        return entries(config.schema.fields);
    }, [config.schema.fields]);

    useEffect(() => {
        // config is always valid
        setConfigurationValid(true);
    });

    const methods = useOmniForm<InferType<TSchema>>({
        mode: "onBlur",
        reValidateMode: "onBlur",
        resolver: yupResolver(config.schema)
    });

    const onSubmit = (input: InferType<TSchema>) => {
        const result = calculate({
            config: config,
            input: input,
            features: features
        });

        if (!result || !isValidPositiveNumber(result)) return;

        const quantity = Math.ceil(result);

        setCalculatedQuantity(quantity);
        updateProductDetail({
            quantity
        });
    };

    return (
        <div
            className={cn(
                "tw:bg-white tw:p-4 tw:border tw:rounded-sm tw:border-grey-input",
                className
            )}
        >
            <p className={cn("tw:font-bold")}>
                <i className={cn("las la-calculator tw:mr-3")}></i>
                {t(`productConfigurationCalculator.${config.id}.title`)}
            </p>
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <OmniFormContextProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onSubmit)}
                            className={cn("tw:flex tw:justify-start tw:gap-2", {
                                "tw:flex-row tw:items-end":
                                    fieldEntries.length <= 1,
                                "tw:flex-col tw:items-stretch":
                                    fieldEntries.length > 1
                            })}
                        >
                            {fieldEntries.map(([name, schema]) => {
                                const description = schema.describe();

                                if (description?.type !== "number") {
                                    return null;
                                }

                                const unit =
                                    "meta" in description
                                        ? description.meta?.unit
                                        : undefined;

                                const optional =
                                    "optional" in description
                                        ? description.optional
                                        : false;

                                return (
                                    <CalculatorField<FieldValues>
                                        key={name}
                                        label={t(
                                            `productConfigurationCalculator.${config.id}.field.${name}.label`,
                                            unit
                                        )}
                                        placeholder={t(
                                            `productConfigurationCalculator.${config.id}.field.${name}.placeholder`,
                                            unit
                                        )}
                                        name={name}
                                        required={!optional}
                                        unit={unit}
                                    />
                                );
                            })}
                            <Button
                                color={CalculatorStyles.submitButton.color}
                                type={"submit"}
                                className={cn("tw:h-fit tw:w-fit")}
                            >
                                {t(
                                    "productConfigurationCalculator.calculate.buttonLabel"
                                )}
                            </Button>
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
            {calculatedQuantity && (
                <>
                    <p className={cn("tw:mb-0 tw:mt-4")}>
                        <span className={cn("tw:font-bold tw:mr-2")}>
                            {t(
                                `productConfigurationCalculator.${config.id}.result`,
                                calculatedQuantity
                            )}
                        </span>
                    </p>
                    <p>
                        {t(
                            `productConfigurationCalculator.${config.id}.disclaimer`
                        )}
                    </p>
                    {product?.price?.currentPrice && (
                        <>
                            <Divider marginTop="0" />
                            <div className={cn("tw:flex tw:gap-3 tw:mt-3")}>
                                <div className={cn("tw:grow")}>
                                    <span className={cn("tw:font-bold")}>
                                        {t(
                                            "productConfigurationCalculator.result.totalPrice"
                                        )}
                                    </span>
                                    <br />
                                    <Price
                                        price={
                                            product.price.currentPrice *
                                            calculatedQuantity
                                        }
                                        size="md"
                                    >
                                        <Price.Main
                                            className={cn("tw:text-black")}
                                        />
                                    </Price>
                                </div>
                                <div className={cn("tw:self-end tw:text-sm")}>
                                    {t(
                                        "productConfigurationCalculator.result.calculation",
                                        calculatedQuantity,
                                        formatPrice(product.price.currentPrice)
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};
